import {
  type Component,
  createSignal,
  createResource,
  Show,
  For
} from "solid-js"
import { type WorkplaceId, type Workplace } from "@eckersdorf/api"
import {
  getWorkplaces,
  createWorkplace,
  updateWorkplace,
  deleteWorkplace
} from "../lib/api"
import { useAuth } from "../lib/auth"

export const StoresPage: Component = () => {
  const { user } = useAuth()
  
  // Fetch workplaces
  const [workplaces, { refetch }] = createResource(user, async (u) => {
    if (!u) return []
    return getWorkplaces()
  })

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = createSignal(false)
  const [editingId, setEditingId] = createSignal<WorkplaceId | null>(null)
  const [submitting, setSubmitting] = createSignal(false)
  
  const [formData, setFormData] = createSignal({
    name: "",
    type: "shop" as "shop" | "dc" | "wholesale",
    email: "",
    streetName: "",
    streetNumber: "",
    houseNumber: "",
    zipCode: "",
    city: ""
  })

  const resetForm = () => {
    setFormData({
      name: "",
      type: "shop",
      email: "",
      streetName: "",
      streetNumber: "",
      houseNumber: "",
      zipCode: "",
      city: ""
    })
    setEditingId(null)
  }

  const openAddModal = () => {
    resetForm()
    setIsModalOpen(true)
  }

  const openEditModal = (wp: Workplace) => {
    setFormData({
      name: wp.name,
      type: wp.type,
      email: wp.email,
      streetName: wp.address.streetName,
      streetNumber: wp.address.streetNumber,
      houseNumber: wp.address.houseNumber ?? "",
      zipCode: wp.address.zipCode,
      city: wp.address.city
    })
    setEditingId(wp.id)
    setIsModalOpen(true)
  }

  const handleDelete = async (wp: Workplace) => {
    if (!confirm(`Czy na pewno chcesz usunąć sklep ${wp.name}?`)) {
      return
    }
    try {
      await deleteWorkplace(wp.id)
      await refetch()
    } catch (e) {
      alert("Nie udało się usunąć sklepu")
      console.error(e)
    }
  }

  const handleSubmit = async (e: Event) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const data = formData()
      const address = {
        streetName: data.streetName,
        streetNumber: data.streetNumber,
        houseNumber: data.houseNumber || null,
        zipCode: data.zipCode as any, // Cast to expected type if needed
        city: data.city
      }

      const input = {
        name: data.name,
        type: data.type,
        email: data.email as any,
        address
      }

      if (editingId()) {
        await updateWorkplace(editingId()!, input)
      } else {
        await createWorkplace(input)
      }
      
      setIsModalOpen(false)
      await refetch()
    } catch (e) {
      console.error(e)
      alert("Błąd zapisu")
    } finally {
      setSubmitting(false)
    }
  }

  // Helper for type label
  const typeLabel = (type: string) => {
    switch (type) {
      case "shop": return "Sklep"
      case "dc": return "Centrum Dystrybucyjne"
      case "wholesale": return "Hurtownia"
      default: return type
    }
  }

  return (
    <div class="space-y-6">
      {/* Header */}
      <div class="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <h2 class="text-xl font-semibold text-gray-900">Zarządzanie Sklepami</h2>
        <button
          onClick={openAddModal}
          class="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors font-medium"
        >
          Dodaj sklep
        </button>
      </div>

      {/* Table */}
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nazwa</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adres</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcje</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <Show when={!workplaces.loading} fallback={
                 <tr><td colspan="5" class="px-6 py-4 text-center">Ładowanie...</td></tr>
              }>
                 <For each={workplaces()} fallback={
                    <tr><td colspan="5" class="px-6 py-4 text-center text-gray-500">Brak sklepów</td></tr>
                 }>
                   {(wp) => (
                     <tr class="hover:bg-gray-50">
                       <td class="px-6 py-4 whitespace-nowrap">
                         <div class="text-sm font-medium text-gray-900">{wp.name}</div>
                       </td>
                       <td class="px-6 py-4 whitespace-nowrap">
                         <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                           {typeLabel(wp.type)}
                         </span>
                       </td>
                       <td class="px-6 py-4 whitespace-nowrap">
                         <div class="text-sm text-gray-500">{wp.email}</div>
                       </td>
                       <td class="px-6 py-4 whitespace-nowrap">
                         <div class="text-sm text-gray-500">
                           {wp.address.streetName} {wp.address.streetNumber}
                           {wp.address.houseNumber ? `/${wp.address.houseNumber}` : ''}
                         </div>
                         <div class="text-xs text-gray-500">
                           {wp.address.zipCode} {wp.address.city}
                         </div>
                       </td>
                       <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                         <button 
                           onClick={() => openEditModal(wp)}
                           class="text-blue-600 hover:text-blue-900 mr-4"
                         >
                           Edytuj
                         </button>
                         <button 
                           onClick={() => handleDelete(wp)}
                           class="text-red-600 hover:text-red-900"
                         >
                           Usuń
                         </button>
                       </td>
                     </tr>
                   )}
                 </For>
              </Show>
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Show when={isModalOpen()}>
        <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setIsModalOpen(false)}></div>

            <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <form onSubmit={handleSubmit}>
                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                    {editingId() ? "Edytuj sklep" : "Dodaj sklep"}
                  </h3>
                  
                  <div class="grid grid-cols-1 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700">Nazwa</label>
                      <input 
                        type="text" 
                        required
                        value={formData().name}
                        onInput={(e) => setFormData({...formData(), name: e.currentTarget.value})}
                        class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                      />
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Typ</label>
                        <select 
                          value={formData().type}
                          onChange={(e) => setFormData({...formData(), type: e.currentTarget.value as any})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        >
                          <option value="shop">Sklep</option>
                          <option value="dc">Centrum Dystrybucyjne</option>
                          <option value="wholesale">Hurtownia</option>
                        </select>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Email</label>
                        <input 
                          type="email" 
                          required
                          value={formData().email}
                          onInput={(e) => setFormData({...formData(), email: e.currentTarget.value})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        />
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                       <div>
                        <label class="block text-sm font-medium text-gray-700">Ulica</label>
                        <input 
                          type="text" 
                          required
                          value={formData().streetName}
                          onInput={(e) => setFormData({...formData(), streetName: e.currentTarget.value})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        />
                       </div>
                       <div class="grid grid-cols-2 gap-2">
                         <div>
                          <label class="block text-sm font-medium text-gray-700">Nr</label>
                          <input 
                            type="text" 
                            required
                            value={formData().streetNumber}
                            onInput={(e) => setFormData({...formData(), streetNumber: e.currentTarget.value})}
                            class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                          />
                         </div>
                         <div>
                          <label class="block text-sm font-medium text-gray-700">Lokal</label>
                          <input 
                            type="text" 
                            value={formData().houseNumber}
                            onInput={(e) => setFormData({...formData(), houseNumber: e.currentTarget.value})}
                            class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                          />
                         </div>
                       </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Kod pocztowy</label>
                        <input 
                          type="text" 
                          required
                          value={formData().zipCode}
                          onInput={(e) => setFormData({...formData(), zipCode: e.currentTarget.value})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Miasto</label>
                        <input 
                          type="text" 
                          required
                          value={formData().city}
                          onInput={(e) => setFormData({...formData(), city: e.currentTarget.value})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        />
                      </div>
                    </div>

                  </div>
                </div>
                <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button 
                    type="submit" 
                    disabled={submitting()}
                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {submitting() ? "Zapisywanie..." : "Zapisz"}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)}
                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Anuluj
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Show>
    </div>
  )
}
